/* Formula resource pages (snippets/ob-res-hero.liquid loads this, deferred).
   1. Filter engine for [data-obr-filter]: comparison chart, ingredient finder, instructions.
   2. Formula sheet dialog: links with [data-obr-sheet] open sections/ob-formula-sheet.liquid,
      fetched with the Section Rendering API. ?formula=<handle> opens a sheet on load. */
(() => {
  'use strict';

  const params = () => new URLSearchParams(location.search);
  const setParams = (fn) => {
    const p = params();
    fn(p);
    const qs = p.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
  };

  /* ---------- 1. Filters ----------
     Items:  [data-obr-item] with data-f-<field>="value" (one value) and data-traits="a b c".
             Search text comes from data-q (falls back to textContent).
     Chips:  [data-obr-chip="<field>"][data-value]   any-of within a field (data-single for one-of)
             [data-obr-chip="traits"][data-value]     three-state: off, with, without (data-only="without"
                                                      makes it a plain "without" toggle)
     Wraps:  [data-obr-wrap] hides itself when none of its items is visible; [data-obr-wrapcount] inside.
     Output: [data-obr-count] [data-obr-empty] [data-obr-reset]                                     */
  function initFilter(root) {
    const items = [...root.querySelectorAll('[data-obr-item]')];
    const wraps = [...root.querySelectorAll('[data-obr-wrap]')];
    const chips = [...root.querySelectorAll('[data-obr-chip]')];
    // Phones: the chip groups fold behind a Filters button (CSS shows it below 750px).
    const bar = root.querySelector('.obr-bar');
    const groupsEl = bar && bar.querySelector('.obr-groups');
    let badge = null;
    if (groupsEl) {
      groupsEl.id = groupsEl.id || 'obr-groups-' + Math.random().toString(36).slice(2, 7);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'obr-filters-btn';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', groupsEl.id);
      btn.innerHTML = '<svg class="obr-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M7 12h10M10 18h4"/></svg><span>Filters</span><span class="obr-filters-btn__n"></span>';
      badge = btn.querySelector('.obr-filters-btn__n');
      bar.querySelector('.obr-bar__top').appendChild(btn);
      bar.classList.add('has-toggle');
      btn.addEventListener('click', () => {
        const open = bar.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    const search = root.querySelector('[data-obr-search]');
    const countEl = root.querySelector('[data-obr-count]');
    const emptyEl = root.querySelector('[data-obr-empty]');
    const resetEl = root.querySelector('.obr-bar [data-obr-reset]') || root.querySelector('[data-obr-reset]');
    const resetAll = [...root.querySelectorAll('[data-obr-reset]')];
    const sync = root.hasAttribute('data-obr-sync');
    const noun = root.dataset.obrNoun || 'formula';
    const fields = {};
    const withT = new Set();
    const withoutT = new Set();

    const data = items.map((el) => {
      const f = {};
      for (const a of el.attributes) if (a.name.startsWith('data-f-')) f[a.name.slice(7)] = a.value;
      return {
        el,
        f,
        traits: new Set((el.dataset.traits || '').split(' ').filter(Boolean)),
        q: (el.dataset.q || el.textContent).toLowerCase().replace(/\s+/g, ' '),
      };
    });

    const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    data.forEach((d) => { d.qn = norm(d.q); });

    function matches(d, skipTrait) {
      for (const k in fields) {
        if (fields[k].size && !fields[k].has(d.f[k])) return false;
      }
      for (const t of withT) if (t !== skipTrait && !d.traits.has(t)) return false;
      for (const t of withoutT) if (t !== skipTrait && d.traits.has(t)) return false;
      const term = search ? norm(search.value.trim()) : '';
      if (term) {
        const words = term.split(/\s+/);
        if (!words.every((w) => d.qn.includes(w))) return false;
      }
      return true;
    }

    function render() {
      let shown = 0;
      data.forEach((d) => {
        const ok = matches(d);
        d.el.hidden = !ok;
        if (ok) shown++;
      });
      wraps.forEach((w) => {
        const n = [...w.querySelectorAll('[data-obr-item]')].filter((i) => !i.hidden).length;
        w.hidden = n === 0;
        const c = w.querySelector('[data-obr-wrapcount]');
        if (c) c.textContent = n + ' ' + (n === 1 ? noun : noun + 's');
      });
      if (countEl) countEl.innerHTML = `<strong>${shown}</strong> of ${data.length}`;
      if (emptyEl) emptyEl.hidden = shown > 0;
      const active = Object.values(fields).some((s) => s.size) || withT.size || withoutT.size || (search && search.value.trim());
      if (resetEl) resetEl.hidden = !active;
      if (badge) {
        const n = Object.values(fields).reduce((a, f) => a + f.size, 0) + withT.size + withoutT.size;
        badge.textContent = n ? n : '';
      }

      // Trait chips show how many of the currently matching formulas contain that ingredient.
      chips.forEach((c) => {
        const n = c.querySelector('.obr-chip__n');
        if (!n || c.dataset.obrChip !== 'traits') return;
        const t = c.dataset.value;
        const pool = data.filter((d) => matches(d, t));
        n.textContent = pool.filter((d) => d.traits.has(t)).length;
      });

      if (sync) writeUrl();
      root.dispatchEvent(new CustomEvent('obr:filtered', { detail: { shown } }));
    }

    function setChipState(c) {
      const k = c.dataset.obrChip;
      const v = c.dataset.value;
      if (k === 'traits') {
        const st = withT.has(v) ? 'with' : withoutT.has(v) ? 'without' : '';
        if (st) c.dataset.state = st; else delete c.dataset.state;
        if (c.hasAttribute('data-only')) c.setAttribute('aria-pressed', st ? 'true' : 'false');
        else c.setAttribute('aria-label', `${c.dataset.label || v}: ${st === 'with' ? 'must contain' : st === 'without' ? 'must not contain' : 'any'}`);
      } else {
        c.setAttribute('aria-pressed', fields[k] && fields[k].has(v) ? 'true' : 'false');
      }
    }

    chips.forEach((c) => {
      const k = c.dataset.obrChip;
      if (k !== 'traits' && !fields[k]) fields[k] = new Set();
      c.addEventListener('click', () => {
        const v = c.dataset.value;
        if (k === 'traits') {
          const only = c.dataset.only;
          if (only === 'without') {
            if (withoutT.has(v)) withoutT.delete(v); else withoutT.add(v);
          } else if (only === 'with') {
            if (withT.has(v)) withT.delete(v); else withT.add(v);
          } else if (withT.has(v)) { withT.delete(v); withoutT.add(v); }
          else if (withoutT.has(v)) withoutT.delete(v);
          else withT.add(v);
        } else {
          const set = fields[k];
          if (c.hasAttribute('data-single')) {
            const had = set.has(v);
            set.clear();
            if (!had) set.add(v);
            chips.filter((o) => o.dataset.obrChip === k).forEach(setChipState);
          } else if (set.has(v)) set.delete(v); else set.add(v);
        }
        setChipState(c);
        render();
      });
      setChipState(c);
    });

    let t;
    if (search) search.addEventListener('input', () => { clearTimeout(t); t = setTimeout(render, 120); });
    resetAll.forEach((r) => r.addEventListener('click', () => {
      Object.values(fields).forEach((s) => s.clear());
      withT.clear(); withoutT.clear();
      if (search) search.value = '';
      chips.forEach(setChipState);
      render();
    }));

    function writeUrl() {
      setParams((p) => {
        ['q', 'with', 'without', ...Object.keys(fields)].forEach((k) => p.delete(k));
        for (const k in fields) if (fields[k].size) p.set(k, [...fields[k]].join(','));
        if (withT.size) p.set('with', [...withT].join(','));
        if (withoutT.size) p.set('without', [...withoutT].join(','));
        if (search && search.value.trim()) p.set('q', search.value.trim());
      });
    }
    function readUrl() {
      const p = params();
      for (const k in fields) (p.get(k) || '').split(',').filter(Boolean).forEach((v) => fields[k].add(v));
      (p.get('with') || '').split(',').filter(Boolean).forEach((v) => withT.add(v));
      (p.get('without') || '').split(',').filter(Boolean).forEach((v) => withoutT.add(v));
      if (search && p.get('q')) search.value = p.get('q');
      chips.forEach(setChipState);
    }

    if (sync) readUrl();
    render();
    root.obrFilter = { render, withT, withoutT, fields };
  }

  /* ---------- 2. Formula sheet dialog ---------- */
  let dialog, body, cache = new Map(), lastTrigger = null;

  function buildDialog() {
    dialog = document.createElement('dialog');
    dialog.className = 'obr-dialog';
    dialog.setAttribute('aria-label', 'Formula preparation sheet');
    dialog.innerHTML = `
      <div class="obr-dialog__panel">
        <div class="obr-dialog__bar">
          <button type="button" class="obr-dialog__btn" data-obr-print>
            <svg class="obr-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            <span>Print</span>
          </button>
          <button type="button" class="obr-dialog__btn" data-obr-close aria-label="Close">
            <svg class="obr-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="obr-dialog__body obc" tabindex="-1"></div>
      </div>`;
    document.body.appendChild(dialog);
    body = dialog.querySelector('.obr-dialog__body');
    dialog.querySelector('[data-obr-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-obr-print]').addEventListener('click', () => {
      document.documentElement.classList.add('obr-printing');
      window.print();
    });
    window.addEventListener('afterprint', () => document.documentElement.classList.remove('obr-printing'));
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      setParams((p) => p.delete('formula'));
      if (lastTrigger) lastTrigger.focus({ preventScroll: true });
    });
    body.addEventListener('click', (e) => {
      const a = e.target.closest('.obr-sheet__nav a');
      if (!a) return;
      e.preventDefault();
      const target = body.querySelector(a.getAttribute('href'));
      if (target) target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    });
  }

  async function openSheet(url, handle, trigger) {
    if (!dialog) buildDialog();
    lastTrigger = trigger || null;
    body.innerHTML = '<div class="obr-dialog__loading" aria-busy="true" aria-label="Loading"><span></span><span></span><span></span><span></span><span></span></div>';
    if (!dialog.open) dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    setParams((p) => p.set('formula', handle));
    try {
      let html = cache.get(handle);
      if (!html) {
        const u = new URL(url, location.origin);
        u.search = '';
        u.searchParams.set('section_id', 'ob-formula-sheet');
        const res = await fetch(u.toString(), { credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const sheet = doc.querySelector('.obr-sheet');
        if (!sheet) throw new Error('no sheet');
        html = sheet.outerHTML;
        cache.set(handle, html);
      }
      body.innerHTML = html;
      body.scrollTop = 0;
      body.focus({ preventScroll: true });
    } catch (err) {
      body.innerHTML = `<div class="obr-empty"><strong>This sheet didn't load</strong><p>Check your connection and try again, or open the product page instead.</p><a class="obc-btn obc-btn--solid" href="${url}">Open the product page</a></div>`;
    }
  }

  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-obr-sheet]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    openSheet(a.getAttribute('href'), a.dataset.obrSheet, a);
  });

  // Preparation video: the thumbnail link becomes the YouTube player on click (no player script until then).
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-obr-video]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    const f = document.createElement('iframe');
    f.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(a.dataset.obrVideo)}?autoplay=1&rel=0`;
    f.title = a.getAttribute('aria-label').replace(/^Play video: /, '');
    f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    f.allowFullscreen = true;
    const box = document.createElement('div');
    box.className = 'obr-video__frame';
    box.appendChild(f);
    a.replaceWith(box);
    f.focus();
  });

  function boot() {
    document.querySelectorAll('[data-obr-filter]').forEach(initFilter);
    const h = params().get('formula');
    if (h && /^[a-z0-9-]+$/.test(h)) {
      const link = document.querySelector(`[data-obr-sheet="${h}"]`);
      openSheet(link ? link.getAttribute('href') : `/products/${h}`, h, link);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.OBR = { openSheet, params, setParams };
})();
